import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { agentsApi } from '../api/agentsApi';
import { agentsKeys, AGENT_POLL_MS } from '../queryKeys';

// A task's chat thread. Only fetched while the panel is open (`enabled`). Sending a message
// invalidates both the thread and the task list (a re-opened closed task becomes active again, so
// useAgentTasks resumes its 3s polling).
export function useTaskThread(taskId: string, locationId: string, enabled: boolean) {
  const qc = useQueryClient();
  const thread = useQuery({
    queryKey: agentsKeys.thread(locationId, taskId),
    queryFn: () => agentsApi.getThread(locationId, taskId),
    enabled: enabled && !!locationId,
    refetchInterval: enabled ? AGENT_POLL_MS : false,
  });
  const send = useMutation({
    mutationFn: (body: string) => agentsApi.sendMessage(locationId, taskId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: agentsKeys.thread(locationId, taskId) });
      qc.invalidateQueries({ queryKey: agentsKeys.tasks(locationId) });
    },
  });
  const retry = useMutation({
    mutationFn: () => agentsApi.retryReply(locationId, taskId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: agentsKeys.thread(locationId, taskId) });
      qc.invalidateQueries({ queryKey: agentsKeys.tasks(locationId) });
    },
  });
  const stop = useMutation({
    mutationFn: () => agentsApi.stopReply(locationId, taskId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: agentsKeys.thread(locationId, taskId) });
      qc.invalidateQueries({ queryKey: agentsKeys.tasks(locationId) });
    },
  });
  return {
    messages: thread.data ?? [],
    isLoading: thread.isLoading,
    send: send.mutate,
    sending: send.isPending,
    retry: retry.mutate,
    retrying: retry.isPending,
    stop: stop.mutate,
    stopping: stop.isPending,
  };
}
